import {
  ADMIN_ROLE,
  AppRole,
  MODERATOR_ROLE,
  PRODUCT_MANAGER_ROLE,
  SALES_MANAGER_ROLE,
  SALES_REP_ROLE,
  getUserRoles,
} from './roleConstants';

export type Permission =
  | 'app.access'
  | 'ops.read'
  | 'ops.write'
  | 'companies.read'
  | 'companies.write'
  | 'contacts.read'
  | 'contacts.write'
  | 'magazine.access'
  | 'magazine.publications.write'
  | 'magazine.catalog.write'
  | 'magazine.reports'
  | 'reports.read'
  | 'deals.read.org'
  | 'deals.write.any'
  | 'deals.write.own'
  | 'contracts.templates'
  | 'bookings.hub'
  | 'users.manage'
  | 'maintenance'
  | 'quickbooks.read'
  | 'quickbooks.manage';

const PM: AppRole[] = [PRODUCT_MANAGER_ROLE, MODERATOR_ROLE];
const SM: AppRole[] = [SALES_MANAGER_ROLE, SALES_REP_ROLE];

const PERMISSION_MATRIX: Record<Permission, AppRole[]> = {
  'app.access': [ADMIN_ROLE, ...PM, ...SM],

  'ops.read': [ADMIN_ROLE, ...PM],
  'ops.write': [ADMIN_ROLE, ...PM],

  'companies.read': [ADMIN_ROLE, ...PM, ...SM],
  'companies.write': [ADMIN_ROLE, ...PM],

  'contacts.read': [ADMIN_ROLE, ...PM, ...SM],
  'contacts.write': [ADMIN_ROLE, ...PM, ...SM],

  'magazine.access': [ADMIN_ROLE, ...PM, ...SM],
  'magazine.publications.write': [ADMIN_ROLE],
  'magazine.catalog.write': [ADMIN_ROLE, ...PM],
  'magazine.reports': [ADMIN_ROLE, ...PM],

  'reports.read': [ADMIN_ROLE, ...PM, ...SM],

  'deals.read.org': [ADMIN_ROLE, ...PM, ...SM],
  'deals.write.any': [ADMIN_ROLE, ...PM],
  'deals.write.own': [ADMIN_ROLE, ...PM, ...SM],

  'contracts.templates': [ADMIN_ROLE],
  'bookings.hub': [ADMIN_ROLE],
  'users.manage': [ADMIN_ROLE],
  maintenance: [ADMIN_ROLE],

  'quickbooks.read': [ADMIN_ROLE],
  'quickbooks.manage': [ADMIN_ROLE],
};

export const ASSIGNABLE_ROLES = [
  { value: 'product_manager', label: 'Product manager' },
  { value: 'sales_manager', label: 'Sales manager' },
] as const;

const normalizeRoles = (roles: string[]): Set<string> => {
  const set = new Set(roles);
  if (set.has('ROLE_MODERATOR')) set.add('ROLE_PRODUCT_MANAGER');
  if (set.has('ROLE_SALES_REP')) set.add('ROLE_SALES_MANAGER');
  return set;
};

export const can = (user: { roles?: string[] } | null, permission: Permission): boolean => {
  const allowed = PERMISSION_MATRIX[permission];
  if (!allowed?.length) return false;
  const userRoles = normalizeRoles(getUserRoles(user));
  return allowed.some((role) => userRoles.has(role));
};

export const canAny = (
  user: { roles?: string[] } | null,
  permissions: Permission[]
): boolean => permissions.some((p) => can(user, p));
